import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { listJobs } from './api';
import { config } from './config';
import { UploadPanel } from './components/UploadPanel';
import { JobCard } from './components/JobCard';
import { IN_PROGRESS, type Job } from './types';

const POLL_INTERVAL_MS = 5000;

export function App() {
  const auth = useAuth();
  const token = auth.user?.id_token;

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadError, setLoadError] = useState<string>();
  const [expandedJobId, setExpandedJobId] = useState<string>();
  const loadedOnce = useRef(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const result = await listJobs(token);
      setJobs(result.jobs);
      setLoadError(undefined);
      loadedOnce.current = true;
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Poll only while something is actually running, so an idle tab stays quiet.
  useEffect(() => {
    if (!jobs.some((job) => IN_PROGRESS.includes(job.status))) return;
    const timer = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [jobs, refresh]);

  // Always reachable, including after a failed callback: drop the stale
  // ?code=/&state= from the URL and start a clean login instead of dead-ending.
  const startLogin = async () => {
    window.history.replaceState({}, '', window.location.pathname);
    try {
      await auth.removeUser();
    } catch {
      // Nothing stored yet - not a problem.
    }
    await auth.signinRedirect();
  };

  if (auth.isLoading) return <CenteredNote>Anmeldung wird geprüft …</CenteredNote>;

  if (!auth.isAuthenticated || !token) {
    return (
      <main className="signin">
        <h1>Meeting Transcriber</h1>
        {auth.error ? (
          <>
            <p className="error">Anmeldung fehlgeschlagen: {auth.error.message}</p>
            <p className="muted small">
              Das passiert meist, wenn eine Anmelde-Adresse erneut geladen wurde. Ein neuer
              Versuch genügt.
            </p>
          </>
        ) : (
          <p>Audioaufnahmen hochladen, Transkript, Zusammenfassung und Todos zurückbekommen.</p>
        )}
        <button className="primary" onClick={() => void startLogin()}>
          {auth.error ? 'Erneut anmelden' : 'Anmelden'}
        </button>
      </main>
    );
  }

  const signOut = () => {
    void auth.removeUser();
    // Cognito uses its own /logout endpoint rather than OIDC end_session.
    const params = new URLSearchParams({
      client_id: config.cognitoClientId,
      logout_uri: config.redirectUri,
    });
    window.location.assign(`${config.cognitoDomain}/logout?${params.toString()}`);
  };

  return (
    <div className="app">
      <header>
        <div>
          <h1>Meeting Transcriber</h1>
          <p className="muted">Transkript · Zusammenfassung · Todos</p>
        </div>
        <div className="header-actions">
          <span className="muted">{auth.user?.profile.email}</span>
          <button onClick={signOut}>Abmelden</button>
        </div>
      </header>

      <UploadPanel token={token} onUploaded={refresh} />

      <section>
        <div className="section-head">
          <h2>Aufnahmen</h2>
          <button onClick={() => void refresh()}>Aktualisieren</button>
        </div>

        {loadError && <p className="error">Liste konnte nicht geladen werden: {loadError}</p>}

        {jobs.length === 0 && loadedOnce.current && !loadError && (
          <p className="muted">Noch nichts hochgeladen.</p>
        )}

        <ul className="job-list">
          {jobs.map((job) => (
            <JobCard
              key={job.jobId}
              job={job}
              token={token}
              expanded={expandedJobId === job.jobId}
              onToggle={() => setExpandedJobId(expandedJobId === job.jobId ? undefined : job.jobId)}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

function CenteredNote({ children }: { children: React.ReactNode }) {
  return (
    <main className="signin">
      <p>{children}</p>
    </main>
  );
}
